import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps, ReactNode } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { NBAGameplayTierBadge } from '@sorare/core/src/components/card/NBAGameplayTierBadge';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { css } from '@sorare/css';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';

import type { NBACardFooter_anyPlayerInterface } from './__generated__/index.graphql.ts';

const badgeStack = css`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  margin: calc(-1 * var(--intermediate-unit)) 0 calc(-1 * var(--half-unit));
`;

const BADGE_HEIGHT = 40;

export const useNBACardFooterEnabled = (sport: Nullable<Sport>) => {
  const {
    flags: { useGamePlayTiers = false },
  } = useFeatureFlags();

  return useGamePlayTiers && sport === Sport.NBA;
};

type Props = {
  anyPlayer: NBACardFooter_anyPlayerInterface;
  score: ReactNode;
  bonus?: ReactNode;
  showStats?: boolean;
  onClickStats?: ComponentProps<typeof AnyPlayerStatsIcon>['onClick'];
  /** Picking this player would bust the lineup's star cap. */
  overStarCap?: boolean;
} & Pick<
  ComponentProps<typeof CardFooter>,
  'matchups' | 'gameOdds' | 'gameDate' | 'extra' | 'onClick'
>;

export const NBACardFooter = ({
  anyPlayer,
  score,
  bonus,
  showStats = true,
  onClickStats,
  overStarCap,
  ...footerProps
}: Props) => {
  const { gameplayTier } = anyPlayer;

  return (
    <CardFooter
      centerChildren
      left={
        showStats ? (
          <AnyPlayerStatsIcon
            backgroundColor="transparent"
            player={anyPlayer}
            onClick={onClickStats}
          />
        ) : null
      }
      right={bonus}
      {...footerProps}
    >
      {gameplayTier ? (
        <div className={badgeStack}>
          <NBAGameplayTierBadge
            anyPlayer={anyPlayer}
            height={BADGE_HEIGHT}
            l10Tab={score}
            overCap={overStarCap}
          />
        </div>
      ) : (
        score
      )}
    </CardFooter>
  );
};

NBACardFooter.fragments = {
  anyPlayerInterface: gql`
    fragment NBACardFooter_anyPlayerInterface on AnyPlayerInterface {
      slug
      gameplayTier
      ...AnyPlayerStatsIcon_anyPlayer
      ...NBAGameplayTierBadge_anyPlayerInterface
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${NBAGameplayTierBadge.fragments.anyPlayerInterface}
  ` as TypedDocumentNode<NBACardFooter_anyPlayerInterface>,
};
